import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Toaster } from './ui/sonner';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Bell, Calendar, AlertTriangle, ChevronRight, Menu, ClipboardList, Sparkles } from 'lucide-react';
import { OfflineBanner } from './OfflineBanner';
import { TrialBanner } from './TrialBanner';
import { BpuUpdateBanner } from './BpuUpdateBanner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchNotifs = async () => {
      try {
        const res = await axios.get(`${API}/notifications`, { withCredentials: true });
        setNotifications(res.data || []);
      } catch { /* ignore */ }
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const highCount = notifications.filter(n => n.priority === 'high').length;
  const totalCount = notifications.length;

  const priorityColors = {
    high: 'bg-red-500',
    medium: 'bg-amber-500',
    low: 'bg-blue-500',
  };

  const typeIcons = {
    rdv: Calendar,
    alert: AlertTriangle,
    bdc: ClipboardList,
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-lg hover:bg-zinc-800 transition-colors" data-testid="notification-bell">
          <Bell className={`w-5 h-5 ${totalCount > 0 ? 'text-white' : 'text-zinc-500'}`} />
          {totalCount > 0 && (
            <span className={`absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${highCount > 0 ? 'bg-red-500' : 'bg-emerald-500'}`}>
              {totalCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 max-w-[calc(100vw-2rem)] bg-zinc-900 border-zinc-800 p-0" align="end">
        <div className="px-4 py-3 border-b border-zinc-800">
          <h3 className="text-sm font-medium text-white">Notifications</h3>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {notifications.length > 0 ? notifications.map(n => {
            const Icon = typeIcons[n.type] || Bell;
            return (
              <button
                key={n.id}
                className="w-full text-left px-4 py-3 hover:bg-zinc-800 transition-colors flex items-start gap-3 border-b border-zinc-800/50 last:border-0"
                onClick={() => { navigate(n.link || '/'); setOpen(false); }}
              >
                <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${priorityColors[n.priority] || 'bg-zinc-500'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{n.title}</p>
                  <p className="text-xs text-zinc-400 mt-0.5">{n.message}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-600 flex-shrink-0 mt-1" />
              </button>
            );
          }) : (
            <div className="px-4 py-8 text-center text-zinc-500 text-sm">
              Aucune notification
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const Layout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  const isArtisan = user?.role === 'artisan';

  // Close drawer when navigating
  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="min-h-screen bg-[#0A0A0B]">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 md:hidden"
          onClick={closeMobile}
          data-testid="mobile-sidebar-backdrop"
        />
      )}

      <Sidebar mobileOpen={mobileOpen} onMobileClose={closeMobile} />

      <div className="md:ml-64 min-h-screen">
        <OfflineBanner />
        <TrialBanner />
        {/* Top bar */}
        <div className="sticky top-0 z-30 h-14 bg-[#0A0A0B]/80 backdrop-blur-lg border-b border-zinc-800/50 flex items-center justify-between px-4 md:px-8">
          <button
            className="md:hidden p-2 rounded-lg hover:bg-zinc-800 transition-colors text-white"
            onClick={() => setMobileOpen(true)}
            aria-label="Ouvrir le menu"
            data-testid="mobile-menu-button"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="md:hidden" />
          <div className="flex items-center gap-2">
            {isArtisan && (
              <div className="flex items-center gap-2" data-testid="artisan-topbar-name">
                <span className="text-xs text-zinc-500 hidden sm:inline">Espace Artisan ·</span>
                <span className="text-sm text-white font-medium truncate max-w-[160px] sm:max-w-none">
                  {user?.name || user?.email}
                </span>
              </div>
            )}
            {!isArtisan && <NotificationBell />}
          </div>
        </div>
        <main className="p-4 md:p-8 md:pt-4">
          <BpuUpdateBanner />
          <Outlet />
        </main>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
};
